/**
 * Écrans membre : Mes Activités, Mes entraînements, Mes inscriptions,
 * Préférences.
 */
import React, { useState } from 'react';
import { api, uuid, enablePush, disablePush } from '../lib/api.js';
import { Spinner, ErrorBox, OfflineNote, Badge, useApi, Intro, IconeCalendrier, IconeCible, IconeLieu } from '../components/common.jsx';
import { fmtDate, fmtDateHeure, fmtDateHeureMaj, capitaliser } from '../lib/format.js';

/** États pour lesquels le membre est considéré comme engagé. */
const ETATS_ACTIFS = ['inscrit', 'liste_attente', 'excuse'];

/** Pastille d'icône : violet = entraînement (cible), jaune = activité (calendrier). */
function BadgeType({ type }) {
  const entrainement = type === 'entrainement';
  return (
    <span className={`adlt-badge-type ${entrainement ? 'adlt-badge-entrainement' : 'adlt-badge-activite'}`}
      role="img" aria-label={entrainement ? 'Entraînement' : 'Activité'}>
      {entrainement ? <IconeCible /> : <IconeCalendrier />}
    </span>
  );
}

/**
 * Ligne unitaire de l'agenda. Le lieu occupe sa propre ligne : accolé à la
 * date, il débordait dès que le nom du lieu était un peu long.
 */
function LigneEvenement({ ev, onEventClick, toast, onChange }) {
  const [busy, setBusy] = useState(false);
  const mienne = ev.mon_inscription;
  const inscrit = mienne && ETATS_ACTIFS.includes(mienne.etat);
  const ouvrable = ev.type === 'activite';

  async function inscrire(e) {
    e.stopPropagation();
    setBusy(true);
    const r = await api(`activites/${ev.activite_id}/inscription`, {
      method: 'POST',
      body: { etat: 'inscrit', remarque: '', nb_invites: 0 },
      queueable: true,
      idempotencyKey: uuid(),
      label: `Inscription — ${ev.titre}`,
    });
    setBusy(false);
    if (r.ok && r.queued) toast?.('Hors ligne : votre inscription sera transmise à la reconnexion.');
    else if (r.ok) {
      toast?.(r.data.etat === 'liste_attente' ? "Activité complète : vous êtes en liste d'attente." : "C'est noté, vous êtes inscrit !");
      onChange?.();
    } else toast?.(r.error, true);
  }

  const contenu = (
    <>
      <BadgeType type={ev.type} />
      <span className="flex-grow-1" style={{ minWidth: 0 }}>
        {ev.titre}
        <br /><small className="text-muted">
          {fmtDateHeureMaj(ev.date)}{ev.ponctuelle ? ' · ★ ponctuel' : ''}
        </small>
        {ev.lieu && (
          <><br /><small className="text-muted d-inline-flex align-items-center gap-1"><IconeLieu taille={12} /> {ev.lieu}</small></>
        )}
      </span>
      {inscrit && <Badge statut={mienne.etat} />}
    </>
  );

  return (
    <div className={`list-group-item ${ouvrable ? 'list-group-item-action' : ''}`}
      role={ouvrable ? 'button' : undefined} tabIndex={ouvrable ? 0 : undefined}
      onClick={ouvrable ? () => onEventClick?.({ type: 'activite', activite_id: ev.activite_id }) : undefined}
      onKeyDown={ouvrable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEventClick?.({ type: 'activite', activite_id: ev.activite_id }); }
      } : undefined}>
      <div className="d-flex align-items-center gap-3">{contenu}</div>
      {/* S'inscrire sans quitter l'agenda. */}
      {ouvrable && !inscrit && ev.inscription_ouverte && (
        <button type="button" className="btn btn-primary btn-sm w-100 mt-2" disabled={busy} onClick={inscrire}>
          {busy ? '…' : "S'inscrire"}
        </button>
      )}
    </div>
  );
}

/**
 * Mes Activités : activités et entraînements fusionnés en une liste
 * chronologique. En tête, les 2 prochaines activités et les 2 prochains
 * entraînements ; le reste suit par date croissante.
 */
export function MesActivites({ me, onEventClick, toast }) {
  // Bornes figées au montage : recalculées à chaque rendu, elles changeraient
  // le chemin passé à useApi et relanceraient la requête en boucle.
  const [plage] = useState(() => {
    const auj = new Date();
    const fin = new Date(Date.now() + 180 * 86400000);
    return { du: auj.toISOString().slice(0, 10), au: fin.toISOString().slice(0, 10) };
  });
  const [activites, rechargerActivites] = useApi(`activites?du=${plage.du}&au=${plage.au}`, { cache: 'cal_activites' });
  const [seances] = useApi(`seances?du=${plage.du}&au=${plage.au}`, { cache: 'cal_seances' });
  if (activites.loading || seances.loading) return <Spinner />;
  if (!activites.ok) return <ErrorBox message={activites.error} />;

  // Normalisation en événements homogènes. Dates ISO locales : le tri
  // lexicographique est fiable.
  const lesActivites = (activites.data || [])
    .filter((a) => a.statut !== 'annulee')
    .map((a) => ({
      key: 'a' + a.id, type: 'activite', date: a.debut, titre: a.titre,
      lieu: a.lieu, activite_id: a.id, mon_inscription: a.mon_inscription,
      inscription_ouverte: a.inscription_ouverte,
    }))
    .sort((x, y) => x.date.localeCompare(y.date));
  const lesEntrainements = (seances.ok ? seances.data : [])
    .filter((e) => e.extendedProps?.statut === 'planifiee')
    .map((e) => ({
      key: 's' + e.id, type: 'entrainement', date: e.start, titre: e.title,
      lieu: e.extendedProps?.lieu, ponctuelle: e.extendedProps?.ponctuelle,
    }))
    .sort((x, y) => x.date.localeCompare(y.date));

  // « Prochainement » mêle les deux : 2 activités et les 3 prochains
  // entraînements. « À venir » ne liste que les activités — les entraînements
  // sont hebdomadaires et noieraient la liste ; ils ont leur propre page.
  const enAvant = [
    ...lesActivites.slice(0, 2),
    ...lesEntrainements.slice(0, 3),
  ].sort((x, y) => x.date.localeCompare(y.date));
  const cles = new Set(enAvant.map((e) => e.key));
  const reste = lesActivites.filter((e) => !cles.has(e.key));

  const ligne = (ev) => (
    <LigneEvenement key={ev.key} ev={ev} onEventClick={onEventClick}
      toast={toast} onChange={rechargerActivites} />
  );

  return (
    <div>
      <h2 className="h4 mb-2">Mes Activités</h2>
      <Intro>Vos prochaines activités et vos prochains entraînements. Touchez une activité
        pour en consulter le détail, ou inscrivez-vous directement depuis la liste.</Intro>
      <OfflineNote result={activites} />
      {enAvant.length === 0 && <p className="text-muted">Rien à l'agenda pour le moment.</p>}
      {enAvant.length > 0 && (
        <div className="adlt-prochainement mb-4">
          <h5 className="mb-2">Prochainement</h5>
          <div className="list-group">{enAvant.map(ligne)}</div>
        </div>
      )}
      {reste.length > 0 && (
        <>
          <h5 className="mb-2">À venir</h5>
          <div className="list-group">{reste.map(ligne)}</div>
        </>
      )}
    </div>
  );
}

/**
 * Mes entraînements : une carte par groupe, avec les cinq prochaines séances.
 *
 * Écran de consultation pour le membre : aucune action de gestion ici. Les
 * moniteurs encadrent leurs groupes depuis « Gestion des entraînements ».
 */
export function MesGroupes() {
  const [state] = useApi('groupes', { cache: 'groupes' });
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  return (
    <div>
      <h2 className="h4 mb-2">Mes entraînements</h2>
      <Intro>Vos groupes d'entraînement et leurs cinq prochaines séances.</Intro>
      <OfflineNote result={state} />
      {state.data.length === 0 && <p className="text-muted">Vous n'êtes rattaché(e) à aucun groupe.</p>}
      <div className="row g-3">
        {state.data.map((g) => {
          // L'API renvoie prochaines_seances (5 max) ; un cache hors ligne
          // antérieur peut encore porter l'ancienne forme au singulier.
          const seances = g.prochaines_seances || (g.prochaine_seance ? [g.prochaine_seance] : []);
          return (
            <div className="col-md-6" key={g.id}>
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <h5 className="card-title mb-1">{g.nom}</h5>
                    <span className="badge bg-primary">{g.nb_inscrits} inscrit{g.nb_inscrits > 1 ? 's' : ''}</span>
                  </div>
                  <p className="card-text text-muted mb-2">
                    Discipline : {g.discipline}{g.categorie_age ? ` · ${g.categorie_age}` : ''}{g.saison ? ` · Saison ${g.saison}` : ''}
                  </p>
                  {seances.length ? (
                    <>
                      <h6 className="mb-1 mt-2">Prochains entraînements</h6>
                      <ul className="list-unstyled mb-2">
                        {seances.map((s, i) => (
                          <li key={i} className="mb-1">
                            <small>{capitaliser(fmtDate(s.date))} · {s.heure_debut}–{s.heure_fin}</small>
                            {s.lieu && (
                              <><br /><small className="text-muted d-inline-flex align-items-center gap-1">
                                <IconeLieu taille={12} /> {s.lieu}
                              </small></>
                            )}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="card-text text-muted mb-2">Aucun entraînement planifié.</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Mes inscriptions à venir.
 *
 * La liste des activités porte désormais `mon_inscription` : l'écran
 * enchaînait auparavant un appel de détail par activité, en série.
 */
export function MesInscriptions({ onOpenActivite }) {
  const [state] = useApi('activites', { cache: 'activites_all' });
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const miennes = state.data.filter((a) => a.mon_inscription && a.mon_inscription.etat !== 'annule');
  return (
    <div>
      <h2 className="h4 mb-2">Mes inscriptions</h2>
      <Intro>Les activités du club auxquelles vous êtes inscrit(e).
        Touchez une ligne pour revoir le détail ou vous désinscrire.</Intro>
      <OfflineNote result={state} />
      {miennes.length === 0 && <p className="text-muted">Aucune inscription à venir.</p>}
      <div className="list-group">
        {miennes.map((a) => (
          <button type="button" key={a.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
            onClick={() => onOpenActivite(a.id)}>
            <span>{a.titre}<br /><small className="text-muted">{fmtDateHeure(a.debut)}{a.lieu ? ' · ' + a.lieu : ''}</small></span>
            <Badge statut={a.mon_inscription.etat} />
          </button>
        ))}
      </div>
    </div>
  );
}

/** Préférences : notifications push par type, iCal, vue par défaut. */
export function Preferences({ me, refreshMe }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const prefs = me.preferences?.push || {};
  const types = [
    ['seance', 'Annulation ou déplacement d\'une séance'],
    ['rappel', 'Rappel à 24 heures'],
    ['liste_attente', 'Promotion depuis la liste d\'attente'],
    ['clubhouse', 'Décision sur une demande de club-house'],
  ];

  async function togglePush(type, value) {
    await api('me/preferences', { method: 'PATCH', body: { push: { [type]: value } } });
    refreshMe();
  }

  async function activerPush() {
    setBusy(true);
    const r = await enablePush(me.vapid_public);
    setBusy(false);
    setMsg(r.ok ? 'Notifications push activées sur cet appareil.' : (r.error || 'Activation impossible.'));
  }

  async function couperPush() {
    setBusy(true);
    await disablePush();
    setBusy(false);
    setMsg('Notifications push désactivées sur cet appareil.');
  }

  async function regenerarIcal() {
    const r = await api('ical/regenerer', { method: 'POST' });
    if (r.ok) { setMsg('Nouvelle URL générée — l\'ancienne est révoquée.'); refreshMe(); }
  }

  return (
    <div>
      <h2 className="h4 mb-2">Préférences</h2>
      <Intro>Gérez vos notifications push et votre abonnement calendrier personnel (iCal).</Intro>
      {msg && <div className="alert alert-info py-2">{msg}</div>}
      <div className="card mb-3">
        <div className="card-body">
          <h5>Notifications push</h5>
          <p className="text-muted">Le push complète le courriel, il ne le remplace pas.</p>
          <div className="mb-3">
            <button className="btn btn-primary btn-sm me-2" onClick={activerPush} disabled={busy}>Activer sur cet appareil</button>
            <button className="btn btn-outline-secondary btn-sm" onClick={couperPush} disabled={busy}>Désactiver</button>
          </div>
          {types.map(([type, label]) => (
            <div className="form-check form-switch" key={type}>
              <input className="form-check-input" type="checkbox" id={'push-' + type}
                checked={prefs[type] !== false}
                onChange={(e) => togglePush(type, e.target.checked)} />
              <label className="form-check-label" htmlFor={'push-' + type}>{label}</label>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <h5>Abonnement calendrier (iCal)</h5>
          <p className="text-muted">Vos séances et vos activités, dans votre agenda personnel. L'URL contient un jeton personnel : ne la partagez pas.</p>
          <input className="form-control mb-2" readOnly value={me.ical_url || ''} onFocus={(e) => e.target.select()} />
          <button className="btn btn-outline-primary btn-sm" onClick={regenerarIcal}>Régénérer le jeton</button>
        </div>
      </div>
    </div>
  );
}
